import { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { stats } from "../data/mock";
import { useInView } from "../hooks/useInView";

const Counter = ({ target, decimals = 0, duration = 1500 }) => {
  const [value, setValue] = useState(0);
  const [ref, isInView] = useInView();
  const startedRef = useRef(false);
  useEffect(() => {
    if (!isInView || startedRef.current) return;
    startedRef.current = true;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isInView, target, duration]);
  return <span ref={ref}>{decimals ? value.toFixed(decimals) : Math.floor(value).toLocaleString("en-IN")}</span>;
};

export const StatsCounter = () => (
  <section className="py-12 lg:py-16 bg-slate-900 text-white relative overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,_rgba(56,189,248,0.15),_transparent_50%)] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
        {stats.map((s, i) => (
          <div key={i} className="text-center" data-testid={`stat-${i}`}>
            <div className="flex items-center justify-center gap-1 mb-2">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight bg-gradient-to-b from-white to-slate-300 bg-clip-text text-transparent">
                <Counter target={s.numeric} decimals={s.decimals || 0} />
              </span>
              {s.suffix && <span className="text-xl sm:text-2xl font-bold text-sky-300">{s.suffix}</span>}
              {s.isStar && <Star className="w-5 h-5 sm:w-6 sm:h-6 text-lime-400 fill-lime-400 ml-0.5" />}
            </div>
            <p className="text-xs sm:text-sm text-slate-400 uppercase tracking-wider font-medium">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
